export function formatMoney(amount?: number | null, currency = 'USD'): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

// ---- Configurable date/time format -----------------------------------------
// The merchant's chosen format (Settings → General). Held at module scope and
// hydrated once at app load so every page renders dates consistently. The
// default matches the spec: "MMM DD at hh:mm a" → e.g. "Jun 30 at 09:45 AM".
export const DATE_FORMATS = [
  { label: 'Jun 30 at 09:45 AM', value: 'MMM DD at hh:mm a' },
  { label: 'Jun 30 at 09 AM', value: 'MMM DD at hh a' },
  { label: 'Jun 30, 2026', value: 'MMM DD, YYYY' },
  { label: 'Jun 30, 2026 09:45 AM', value: 'MMM DD, YYYY hh:mm a' },
  { label: '2026-06-30', value: 'YYYY-MM-DD' },
  { label: '06/30/2026', value: 'MM/DD/YYYY' },
] as const;

export const DEFAULT_DATE_FORMAT = 'MMM DD at hh:mm a';
let activeDateFormat: string = DEFAULT_DATE_FORMAT;

/** Set the app-wide date format (called once from the settings hydrator). */
export function setDateFormat(fmt?: string | null): void {
  if (fmt && DATE_FORMATS.some((f) => f.value === fmt)) activeDateFormat = fmt;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');

function renderDate(d: Date, fmt: string): string {
  const mon = MONTHS[d.getMonth()];
  const dd = pad(d.getDate());
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const h24 = d.getHours();
  const ampm = h24 < 12 ? 'AM' : 'PM';
  const hh = pad(((h24 + 11) % 12) + 1); // 12-hour, zero-padded
  const min = pad(d.getMinutes());
  switch (fmt) {
    case 'MMM DD, YYYY': return `${mon} ${dd}, ${yyyy}`;
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
    case 'MMM DD at hh a': return `${mon} ${dd} at ${hh} ${ampm}`;
    case 'MMM DD, YYYY hh:mm a': return `${mon} ${dd}, ${yyyy} ${hh}:${min} ${ampm}`;
    case 'MMM DD at hh:mm a':
    default: return `${mon} ${dd} at ${hh}:${min} ${ampm}`;
  }
}

/** Format a date using the merchant's chosen format (used across all pages). */
export function formatDateTime(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return renderDate(d, activeDateFormat);
}

/** Alias — same configurable format. */
export function formatDate(ms?: number | null): string {
  return formatDateTime(ms);
}

/** Date-only rendering (no time), e.g. "Jul 26, 2026" — used for expiry dates. */
export function formatDateOnly(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()}`;
}

export function formatNumber(n?: number | null): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat().format(n);
}

type Tone = 'success' | 'info' | 'warning' | 'critical' | 'attention' | undefined;

const STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  PAID: 'success',
  FULFILLED: 'success',
  completed: 'success',
  running: 'info',
  queued: 'info',
  pending: 'info',
  info: 'info',
  DRAFT: 'warning',
  UNFULFILLED: 'warning',
  PENDING: 'warning',
  warn: 'warning',
  failed: 'critical',
  error: 'critical',
  REFUNDED: 'critical',
};

export function statusTone(status?: string | null): Tone {
  if (!status) return undefined;
  return STATUS_TONE[status] ?? 'attention';
}
